import React from 'react';
import { PageHeader, SegmentedControl } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useUrlState } from '../../lib/urlState';
import { useShortcut } from '../../lib/shortcuts';
import VendorsView, { VENDOR_STATUSES } from './VendorsView';
import ListingsView, { LISTING_STATUSES } from './ListingsView';
import SettlementsView, { SETTLEMENT_STATUSES } from './SettlementsView';
import SettingsCard from './SettingsCard';

export const VIEWS = ['vendors', 'listings', 'settlements'];

/**
 * The URL contract. `status` is shared by vendors and listings (each view
 * validates its own values); settlements use `sstatus` because «باز» is not a
 * review state. Defaults are absent from the address.
 */
const SCHEMA = {
  view: { default: 'vendors', values: VIEWS },
  status: { default: '', values: ['', ...new Set([...VENDOR_STATUSES, ...LISTING_STATUSES])] },
  sstatus: { default: '', values: SETTLEMENT_STATUSES },
  period: { default: '' },
  vendor: { default: '' },
  listing: { default: '' },
  settlement: { default: '' },
};

/** What a view owns; switching views clears it. */
const VIEW_LOCAL = { status: '', sstatus: '', period: '', vendor: '', listing: '', settlement: '' };

/**
 * «بازار» — the platform's Marketplace review (design
 * docs/CLINIC_STORE_MARKETPLACE_DESIGN.md §7.11, §13).
 *
 * ① PageHeader with the three views (`semantics="tabs"`) · ⑤ P2: the view in
 * the main column, the platform settings (commission + taxonomy) in the rail,
 * which drops under the main column below the wide tier.
 */
export default function Marketplace() {
  const { t } = useTranslation();
  useDocumentTitle('nav.title.marketplace');
  const [view, setView] = useUrlState(SCHEMA);

  const go = React.useCallback((patch) => setView(patch), [setView]);
  const changeView = React.useCallback((next) => {
    if (next !== view.view) setView({ ...VIEW_LOCAL, view: next });
  }, [setView, view.view]);

  VIEWS.forEach((name, i) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useShortcut(String(i + 1), () => changeView(name), {
      label: t('shortcuts.action.view', { view: t(`marketplace.views.${name}`) }),
      group: t('shortcuts.groups.marketplace'),
    });
  });

  // A listing status the vendors view does not know (and vice versa) is junk.
  const scoped = {
    ...view,
    status: view.view === 'vendors'
      ? (VENDOR_STATUSES.includes(view.status) ? view.status : '')
      : (LISTING_STATUSES.includes(view.status) ? view.status : ''),
  };

  return (
    <div className="relative flex h-full flex-col bg-background" data-testid="marketplace">
      <PageHeader title={t('marketplace.title')} sub={t('marketplace.sub')}>
        <SegmentedControl
          semantics="tabs"
          size="sm"
          ariaLabel={t('marketplace.views.label')}
          options={VIEWS.map((value) => ({ value, label: t(`marketplace.views.${value}`) }))}
          value={view.view}
          onChange={changeView}
          data-testid="marketplace-tabs"
        />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto grid w-full max-w-[1440px] gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
          <div role="tabpanel" aria-label={t(`marketplace.views.${view.view}`)} className="min-w-0" data-testid={`marketplace-panel-${view.view}`}>
            {view.view === 'vendors' ? (
              <VendorsView view={scoped} go={go} />
            ) : view.view === 'listings' ? (
              <ListingsView view={scoped} go={go} />
            ) : (
              <SettlementsView view={scoped} go={go} />
            )}
          </div>
          <SettingsCard />
        </div>
      </div>
    </div>
  );
}
